import axios from 'axios';
import { buildApiHeaders, readAuthToken } from '../api/client';
import { notifySessionUnauthorized } from '../auth/sessionEvents';
import type { SessionCreate, SessionResponse } from '../types/session';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

console.log('API_BASE_URL', API_BASE_URL);
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    // Skip ngrok browser warning when using ngrok tunnels
    'ngrok-skip-browser-warning': 'true',
  },
});

api.interceptors.request.use((config) => {
  const merged = buildApiHeaders(config.headers as HeadersInit);
  if (merged instanceof Headers) {
    merged.forEach((value, key) => {
      config.headers.set(key, value);
    });
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401 && readAuthToken()) {
      notifySessionUnauthorized();
    }
    return Promise.reject(err);
  },
);

/** Part A: Settings → Users & access (not Admin Portal). */
export type UserAccessAssignment = {
  assignment_id: string;
  entity_id: string;
  config_id: string | null;
  access_level: string;
};

export type UserAccessRow = {
  user_id: number;
  username: string;
  display_name: string;
  role: string;
  is_active: boolean;
  /** False when user was soft-removed from this tenant (user_tenants.is_active = false). */
  tenant_membership_active?: boolean;
  can_open_settings: boolean;
  can_open_test_dhis2: boolean;
  can_open_admin_portal: boolean;
  allowed_main_tabs: string[] | null;
  tenant_role_name?: string | null;
  doctor_id?: number | null;
  assignments?: UserAccessAssignment[];
  permission_codes?: string[];
  legacy_no_tenant_membership?: boolean;
  last_login_at?: string | null;
};

export type PermissionCatalogItem = {
  code: string;
  description?: string | null;
  group: string;
};

export type TenantRoleOut = {
  role_id: string;
  name: string;
  description?: string | null;
  permission_codes: string[];
};

export const userAccessAPI = {
  listUsers: (tenantId: string, includeLegacyDoctors = true, includeInactiveTenantMemberships = false) =>
    api.get<UserAccessRow[]>('/api/user-access/users', {
      params: {
        tenant_id: tenantId,
        include_legacy_doctors: includeLegacyDoctors,
        include_inactive_tenant_memberships: includeInactiveTenantMemberships,
      },
    }),
  createUser: (body: {
    username: string;
    password: string;
    display_name: string;
    tenant_id: string;
    entity_id: string;
    config_ids?: string[] | null;
    role?: string;
    tenant_role_name?: string;
    permission_codes?: string[] | null;
    can_open_settings?: boolean;
    can_open_test_dhis2?: boolean;
    can_open_admin_portal?: boolean;
    allowed_main_tabs?: string[] | null;
  }) => api.post<{ ok: boolean; user_id: number; username: string }>('/api/user-access/users', body),
  patchUser: (userId: number, tenantId: string, body: Record<string, unknown>) =>
    api.patch(`/api/user-access/users/${userId}`, body, { params: { tenant_id: tenantId } }),
  deleteUser: (userId: number, tenantId: string) =>
    api.delete<{ ok: boolean; user_id: number }>(`/api/user-access/users/${userId}`, {
      params: { tenant_id: tenantId },
    }),
  reactivateUser: (userId: number, tenantId: string) =>
    api.post<{ ok: boolean; user_id: number; reactivated_in_tenant: string }>(
      `/api/user-access/users/${userId}/reactivate`,
      {},
      { params: { tenant_id: tenantId } },
    ),
  linkLegacyUser: (userId: number, body: { tenant_id: string; entity_id: string; config_ids?: string[] | null; role_name?: string }) =>
    api.post(`/api/user-access/users/${userId}/link`, body),
  listEntityConfigs: (tenantId: string, entityId: string) =>
    api.get<Array<{ config_id: string; name: string; schema_source?: string; config_type?: string }>>('/api/user-access/entity-configs', {
      params: { tenant_id: tenantId, entity_id: entityId },
    }),
  getPermissionsCatalog: (tenantId: string) =>
    api.get<PermissionCatalogItem[]>('/api/user-access/permissions-catalog', { params: { tenant_id: tenantId } }),
  getTenantRoles: (tenantId: string) =>
    api.get<TenantRoleOut[]>('/api/user-access/tenant-roles', { params: { tenant_id: tenantId } }),
  removeAssignments: (userId: number, tenantId: string, assignment_ids: string[]) =>
    api.post<{ ok: boolean }>(`/api/user-access/users/${userId}/assignments/remove`, { assignment_ids }, { params: { tenant_id: tenantId } }),
};

export const authAPI = {
  impersonate: (userId: number) =>
    api.post<{
      access_token: string;
      token_type: string;
      user: { id: number; username: string; displayName?: string; role?: string; doctor_id?: number | null };
      access?: unknown;
      impersonated_by_user_id?: number;
    }>(`/api/auth/impersonate/${userId}`),
};

// Session APIs
export const sessionAPI = {
  create: (data: SessionCreate) =>
    api.post<SessionResponse>('/sessions', data),

  get: (sessionId: string) =>
    api.get<SessionResponse>(`/sessions/${sessionId}`),

  list: () =>
    api.get<SessionResponse[]>('/sessions'),

  complete: (sessionId: string) =>
    api.post(`/sessions/${sessionId}/complete`),

  pause: (sessionId: string) =>
    api.post(`/sessions/${sessionId}/pause`),

  patch: (sessionId: string, data: { config_id?: string }) =>
    api.patch<SessionResponse>(`/sessions/${sessionId}`, data),

  // Hybrid Audio - Cabin Management APIs
  getCabins: () =>
    api.get('/sessions/cabins'),

  getAvailableCabins: () =>
    api.get('/sessions/cabins/available'),

  getCabin: (cabinId: string) =>
    api.get(`/sessions/cabins/${cabinId}`),
};

// Analytics APIs
export const analyticsAPI = {
  getSession: (sessionId: string) =>
    api.get(`/analytics/session/${sessionId}`),

  getClinician: (clinicianId: string, days = 30) =>
    api.get(`/analytics/clinician/${clinicianId}`, { params: { days } }),

  getPopulation: (days = 90) =>
    api.get('/analytics/population', { params: { days } }),
};

export const labReportsAPI = {
  getBySession: (sessionId: string) =>
    api.get(`/api/lab-reports/session/${sessionId}`),
};

export type PrescriptionUploadMedication = {
  drug_name: string;
  dose?: string | null;
  unit?: string | null;
  frequency?: string | null;
  route?: string | null;
  duration?: string | null;
  medication_instructions?: string | null;
  confidence?: number | null;
};

export type PrescriptionUploadResponse = {
  success?: boolean;
  upload_id: string;
  status: string;
  confidence: number;
  medications: PrescriptionUploadMedication[];
  warnings: string[];
  uploaded_at?: string;
  extraction_method?: string;
  user_message?: string;
  error_code?: string;
  review_status?: string;
};

export type PrescriptionUploadSummary = {
  upload_id: string;
  session_id: string | null;
  patient_id?: string | number | null;
  status: string;
  confidence: number;
  medication_count: number;
  uploaded_at?: string;
  created_at?: string;
  filename?: string;
  file_type?: string;
  review_status?: string;
};

export type PrescriptionUploadImage = {
  page_number: number;
  image_data_base64: string;
  format: string;
  width: number;
  height: number;
};

export const prescriptionUploadAPI = {
  uploadPrescription: (
    formData: FormData,
    onUploadProgress?: (percent: number) => void,
  ) =>
    api.post<PrescriptionUploadResponse>('/api/prescriptions/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (!onUploadProgress || !evt.total) return;
        const percent = Math.min(100, Math.round((evt.loaded * 100) / evt.total));
        onUploadProgress(percent);
      },
    }),
  getPrescriptionUpload: (uploadId: string) =>
    api.get<PrescriptionUploadResponse>(`/api/prescriptions/uploads/${uploadId}`),
  getSessionPrescriptionUploads: (sessionId: string) =>
    api.get<{ session_id: string; uploads: PrescriptionUploadSummary[] }>(
      `/api/prescriptions/session/${sessionId}`,
    ),
  getAllPrescriptionUploads: (params?: { patient_id?: string | number; limit?: number; offset?: number }) =>
    api.get<{ uploads: PrescriptionUploadSummary[]; total: number; limit: number; offset: number }>(
      '/api/prescriptions/all',
      { params },
    ),
  getPrescriptionUploadPdfUrl: (uploadId: string) =>
    `${API_BASE_URL}/api/prescriptions/uploads/${uploadId}/pdf`,
  getPrescriptionUploadImages: (uploadId: string) =>
    api.get<{ upload_id: string; total_pages: number; images: PrescriptionUploadImage[] }>(
      `/api/prescriptions/uploads/${uploadId}/images`,
    ),
  getPrescriptionUploadHtmlPreview: (uploadId: string) =>
    api.get<{ upload_id: string; html: string }>(`/api/prescriptions/uploads/${uploadId}/preview`),
  approvePrescriptionUpload: (
    uploadId: string,
    payload: { session_id: string; medications: PrescriptionUploadMedication[] },
  ) => api.post(`/api/prescriptions/uploads/${uploadId}/approve`, payload),
  rejectPrescriptionUpload: (uploadId: string, reason?: string) => {
    const formData = new FormData();
    if (reason) formData.append('reason', reason);
    return api.post(`/api/prescriptions/uploads/${uploadId}/reject`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// DHIS2 lab report upload (separate pipeline; uses Lab Mapper + field_synonyms; streams to ws/forms/dhis2 when session_id set)
export const dhis2LabReportsAPI = {
  upload: (configId: string, file: File, sessionId?: string | null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('config_id', configId);
    if (sessionId) formData.append('session_id', sessionId);
    return api.post<{
      file_id: string;
      filename: string;
      config_id: string;
      config_name?: string;
      session_id?: string;
      status: string;
      extractions: Array<{ section: string; field: string; value: unknown; confidence: number; source_text?: string; unit?: string }>;
      normalized_observations: unknown[];
      document_state: Record<string, unknown>;
      raw_text_preview: string;
      confidence: number;
      uploaded_at: string;
    }>('/api/dhis2/lab-reports/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// DHIS2 v4 Config APIs (Phase 2 Admin UI, Phase 4 drift/review)
export interface Dhis2ConfigOut {
  id: string;
  name: string;
  schema_source: string;
  form_schema_json: Record<string, unknown> | null;
  field_mappings_json: Record<string, unknown> | null;
  extraction_prompt_text: string | null;
  /** Web form adapter when dynamic_form or admin_defined */
  config_type?: string | null;
  /** Phase 4: schema drift */
  metadata_hash?: string | null;
  metadata_fetched_at?: string | null;
  schema_drift_detected_at?: string | null;
  review_before_push?: boolean;
}

export interface Dhis2ConnectionOut {
  id: string;
  config_id: string;
  base_url: string | null;
  auth_type: string;
  username: string | null;
  system_info_path: string | null;
  metadata_endpoint: string | null;
  program_specific_url_template: string | null;
  program_id_filter: string | null;
  /** Multiple program UIDs for schema pull (optional; when set, used for multi-program support). */
  program_ids: string[] | null;
  cache_ttl_seconds: number | null;
  timeout_seconds: number | null;
  programdata_api_url: string | null;
  programdata_http_method: string | null;
  programdata_auth_same_as_dhis2: boolean | null;
}

export const dhis2ConfigAPI = {
  listConfigs: () => api.get<Dhis2ConfigOut[]>('/dhis2-configs/'),
  getConfig: (configId: string) => api.get<Dhis2ConfigOut>(`/dhis2-configs/${configId}`),
  createConfig: (data: {
    name: string;
    schema_source?: string;
    form_schema_json?: object;
    field_mappings_json?: object;
    extraction_prompt_text?: string;
    /** Requires X-Tenant-ID when set */
    entity_id?: string;
  }) => api.post<Dhis2ConfigOut>('/dhis2-configs/', data),
  updateConfig: (configId: string, data: Partial<{ name: string; schema_source: string; form_schema_json: object; field_mappings_json: object; extraction_prompt_text: string; review_before_push: boolean }>) =>
    api.put<Dhis2ConfigOut>(`/dhis2-configs/${configId}`, data),
  deleteConfig: (configId: string) => api.delete(`/dhis2-configs/${configId}`),
  cloneConfig: (configId: string, data?: { name?: string; entity_id?: string }) =>
    api.post<Dhis2ConfigOut>(`/dhis2-configs/${configId}/clone`, data ?? {}),
  getWebOpdDefaultTemplate: () =>
    api.get<{ form_schema_json: Record<string, unknown>; extraction_prompt_text: string; field_mappings_json: Record<string, unknown> }>('/dhis2-configs/templates/web-opd-default'),
  applyWebOpdTemplate: (configId: string) =>
    api.post<Dhis2ConfigOut>(`/dhis2-configs/${configId}/apply-web-opd-template`),

  getConnection: (configId: string) => api.get<Dhis2ConnectionOut | null>(`/dhis2-configs/${configId}/connection`),
  setConnection: (configId: string, data: Partial<{
    base_url: string | null;
    auth_type: string;
    api_token_encrypted: string;
    username: string | null;
    system_info_path: string | null;
    metadata_endpoint: string | null;
    program_specific_url_template: string | null;
    program_id_filter: string | null;
    program_ids: string[] | null;
    cache_ttl_seconds: number | null;
    timeout_seconds: number | null;
    programdata_api_url: string | null;
    programdata_http_method: string;
    programdata_auth_same_as_dhis2: boolean;
  }>) => api.put<Dhis2ConnectionOut>(`/dhis2-configs/${configId}/connection`, data),

  getSchema: (configId: string) => api.get<Record<string, unknown>>(`/dhis2-configs/${configId}/schema`),
  putSchema: (configId: string, body: object) => api.put<Record<string, unknown>>(`/dhis2-configs/${configId}/schema`, body),
  getFieldMappings: (configId: string) => api.get<Record<string, unknown>>(`/dhis2-configs/${configId}/field-mappings`),
  putFieldMappings: (configId: string, body: object) => api.put<Record<string, unknown>>(`/dhis2-configs/${configId}/field-mappings`, body),
  getPrompts: (configId: string) =>
    api.get<{
      extraction_prompt_text: string;
      lab_report_extraction_prompt_text: string;
      dhis2_live_summary_prompt_text: string;
    }>(`/dhis2-configs/${configId}/prompts`),
  putPrompts: (
    configId: string,
    body: {
      extraction_prompt_text?: string;
      lab_report_extraction_prompt_text?: string;
      dhis2_live_summary_prompt_text?: string;
    },
  ) =>
    api.put<{
      extraction_prompt_text: string;
      lab_report_extraction_prompt_text: string;
      dhis2_live_summary_prompt_text: string;
    }>(`/dhis2-configs/${configId}/prompts`, body),
  generateExtractionPrompt: (
    configId: string,
    body: { current_prompt: string; change_instructions: string },
  ) =>
    api.post<{ extraction_prompt_text: string }>(
      `/dhis2-configs/${configId}/prompts/generate-extraction`,
      body,
    ),

  getMetadata: (configId: string, updateDrift = true) =>
    api.get<unknown>(`/dhis2-configs/${configId}/dhis2/metadata`, { params: { update_drift: updateDrift } }),
  getProgramData: (configId: string) => api.get<unknown>(`/dhis2-configs/${configId}/dhis2/program-data`),
  testConnection: (configId: string, body?: { base_url?: string; api_token?: string; username?: string; timeout_seconds?: number; system_info_path?: string }) =>
    api.post<{ success?: boolean; message?: string; version?: string; error?: string; detail?: string }>(
      `/dhis2-configs/${configId}/dhis2/test-connection`,
      body ?? {}
    ),

  /** Phase 4: schema drift */
  getSchemaDrift: (configId: string) =>
    api.get<{ needs_review: boolean; schema_drift_detected_at: string | null; metadata_fetched_at: string | null }>(`/dhis2-configs/${configId}/schema-drift`),
  refreshMetadata: (configId: string) =>
    api.post<unknown>(`/dhis2-configs/${configId}/refresh-metadata`),

  getFieldSynonyms: (configId: string) => api.get<Array<{ section_id: string; field_id: string; synonym: string; source: string }>>(`/dhis2-configs/${configId}/field-synonyms`),
  putFieldSynonyms: (configId: string, body: Array<{ section_id: string; field_id: string; synonym: string; source?: string }>) =>
    api.put(`/dhis2-configs/${configId}/field-synonyms`, body),
  suggestSynonyms: (configId: string, body: { section_id: string; field_id: string; field_name?: string; field_description?: string }) =>
    api.post<{ suggestions: string[] }>(`/dhis2-configs/${configId}/fields/suggest-synonyms`, body),
};

// DHIS2 v4 Phase 3 – Sync push and Sync History; Phase 4 enqueue
export const dhis2SyncAPI = {
  push: (data: { config_id: string; session_id?: string; action: 'create_event' | 'update_event'; payload: Record<string, unknown> }) =>
    api.post<{ success: boolean; status_code?: number; event_id?: string; error?: string }>('/dhis2-sync/push', data),
  enqueue: (data: { config_id: string; session_id?: string; adapter_type?: string; action: string; payload: Record<string, unknown>; max_attempts?: number }) =>
    api.post<{ id: string; status: string }>('/dhis2-sync/enqueue', data),
  history: (params?: { config_id?: string; session_id?: string; limit?: number }) =>
    api.get<{ entries: Array<{ id: string; config_id: string; session_id: string | null; action: string; status_code: number | null; event_id: string | null; error_message: string | null; created_at: string | null }> }>('/dhis2-sync/history', { params }),
  health: (params?: { hours?: number }) =>
    api.get<{ total: number; success: number; failed: number; success_rate_pct: number; alert?: boolean; alert_message?: string }>('/dhis2-sync/health', { params }),
};

// DHIS2 v4 System config (LLM, ASR, Diarization, Resources, Deployment)
export const systemConfigAPI = {
  list: () => api.get<Array<{ config_key: string; config_value_json: Record<string, unknown> }>>('/system-config/'),
  get: (configKey: string) =>
    api.get<{ config_key: string; config_value_json: Record<string, unknown> }>(`/system-config/${configKey}`),
  set: (configKey: string, configValueJson: Record<string, unknown>) =>
    api.put<{ config_key: string; config_value_json: Record<string, unknown> }>(`/system-config/${configKey}`, {
      config_value_json: configValueJson,
    }),
  /** Fetch available models for the given LLM provider (Groq, Ollama, etc.). */
  listLlmModels: (body: { provider: string; api_key?: string; base_url?: string }) =>
    api.post<{ models: string[] }>('/system-config/llm/list-models', body),
  /** Reload ASR config from DB and re-initialize the ASR engine (no backend restart needed). */
  reloadAsr: () => api.post<{ ok: boolean; message: string }>('/system-config/reload-asr'),
  /** Scoped ASR config (global, web_app, dhis2). */
  getAsrScoped: (scope: 'global' | 'web_app' | 'dhis2' = 'global') =>
    api.get<{ config_key: string; config_value_json: Record<string, unknown> }>('/system-config/asr', { params: { scope } }),
  setAsrScoped: (scope: 'global' | 'web_app' | 'dhis2', configValueJson: Record<string, unknown>) =>
    api.put<{ config_key: string; config_value_json: Record<string, unknown> }>('/system-config/asr', {
      scope,
      config_value_json: configValueJson,
    }),
  getAsrDhis2Config: (configId: string) =>
    api.get<{ config_key: string; config_value_json: Record<string, unknown> }>(`/system-config/asr/dhis2/${configId}`),
  setAsrDhis2Config: (configId: string, configValueJson: Record<string, unknown>) =>
    api.put<{ config_key: string; config_value_json: Record<string, unknown> }>(`/system-config/asr/dhis2/${configId}`, {
      config_value_json: configValueJson,
    }),
  getAsrWebAppConfig: (configId: string) =>
    api.get<{ config_key: string; config_value_json: Record<string, unknown> }>(`/system-config/asr/web-app/${configId}`),
  setAsrWebAppConfig: (configId: string, configValueJson: Record<string, unknown>) =>
    api.put<{ config_key: string; config_value_json: Record<string, unknown> }>(`/system-config/asr/web-app/${configId}`, {
      config_value_json: configValueJson,
    }),
  getAsrEngineTypes: () =>
    api.get<{ engine_types: Array<'whisperlivekit' | 'indic_whisper' | 'sarvam_ai'> }>('/system-config/asr/engine-types'),
  /** Return all AI prompts (system_config rows with key prefix 'prompts.'). */
  getAiPrompts: () =>
    api.get<Array<{ key: string; config_key: string; value: { text: string; version?: string; category?: string; description?: string } }>>('/system-config/ai-prompts'),
  /** Seed any missing default prompts into system_config. */
  seedAiPrompts: () =>
    api.post<{ ok: boolean; inserted: number }>('/system-config/ai-prompts/seed'),
  /** Invalidate in-memory prompt cache for a key after admin edit. */
  invalidatePromptCache: (key: string) =>
    api.post<{ ok: boolean; invalidated: string }>(`/system-config/ai-prompts/invalidate/${key}`),
};

export const extractionFeedbackAPI = {
  getSessionRows: (sessionId: string) =>
    api.get<{ session_id: string; rows: Array<{ pipeline: 'extracted_fields' | 'dhis2_stream_extractions'; row_id: string; section_id: string; field_id: string; field_name: string; value: unknown; confidence?: number | null; is_correct?: boolean | null; remark?: string | null }>; ambient_experience_rating?: 'Bad' | 'Okay' | 'Good' | 'Excellent' | null; general_feedback?: string | null }>(`/api/extraction-feedback/sessions/${sessionId}`),
  patchSessionRows: (
    sessionId: string,
    updates: Array<{ pipeline: 'extracted_fields' | 'dhis2_stream_extractions'; row_id: string; section_id?: string; field_id?: string; is_correct?: boolean | null; remark?: string | null }>,
    ambient_experience_rating?: 'Bad' | 'Okay' | 'Good' | 'Excellent' | null,
    general_feedback?: string | null,
  ) =>
    api.patch<{ ok: boolean; updated: number }>(`/api/extraction-feedback/sessions/${sessionId}`, { session_id: sessionId, updates, ambient_experience_rating, general_feedback }),
};

/** DSM reference + ICD-11 resolution (migration 048 + dsmv_* tables). added for dsmv_icd11 integration on 13th April 2026 */
export type DiagnosisDisorderSearchRow = {
  disorder_id: number;
  disorder_name?: string | null;
  icd_10?: string | null;
  icd_11?: string | null;
  category_id?: number | null;
  category_name?: string | null;
};

export type DiagnosisModifierRow = {
  pmo_id: number;
  property_id: number;
  property_key?: string | null;
  property_value?: string | null;
  icd_9_override?: string | null;
  icd_10_override?: string | null;
  icd_11_override?: string | null;
  modifier_row_notes?: string | null;
};

export type DiagnosisDisorderDetail = {
  disorder_id: number;
  disorder_name?: string | null;
  icd_9?: string | null;
  icd_10?: string | null;
  icd_11?: string | null;
  notes?: string | null;
  category_id?: number | null;
  category_name?: string | null;
  modifiers: DiagnosisModifierRow[];
};

export type DiagnosisIcd11Alternate = {
  icd11_code: string;
  icd11_title?: string | null;
  map_type: string;
  map_priority: number;
  notes?: string | null;
};

export type DiagnosisResolveIcd11Response = {
  primary_code?: string | null;
  primary_source: string;
  icd10_code?: string | null;
  disorder_name?: string | null;
  category_name?: string | null;
  alternates: DiagnosisIcd11Alternate[];
};

export type SessionDiagnosisSelection = {
  id: string;
  session_id: string;
  slot: string;
  dsmv_disorder_id?: number | null;
  pmo_id?: number | null;
  icd11_code?: string | null;
  icd10_code?: string | null;
  diagnosis_status?: string | null;
  ai_suggestion_accepted?: boolean | null;
  assessment_text?: string | null;
  notes?: string | null;
  finalized_by_user_id?: number | null;
  finalized_at?: string | null;
};

export type DxRunTriggerResponse = {
  triggered: boolean;
  reason?: string | null;
  run_id?: string | null;
  status?: string | null;
  candidate_count?: number | null;
  mapping_count?: number | null;
  elapsed_ms?: number | null;
  debounce_ms?: number | null;
};

export type DxRunSummary = {
  id: string;
  status: string;
  started_at: string;
  completed_at?: string | null;
  metadata_json?: Record<string, unknown> | null;
};

export type DxCandidateMap = {
  mapped_condition_id: number;
  mapped_code: string;
  mapped_title: string;
  score?: number | null;
  map_source: string;
  rationale?: string | null;
};

export type DxCandidate = {
  candidate_id: number;
  slot: string;
  condition_id: number;
  condition_code: string;
  condition_title: string;
  score?: number | null;
  rationale_json?: Record<string, unknown> | null;
  evidence_json?: Record<string, unknown> | null;
  rule_flags_json?: Record<string, unknown> | null;
  maps: DxCandidateMap[];
};

export type DxFinalSelection = {
  id: string;
  session_id: string;
  slot: string;
  selected_condition_id?: number | null;
  selected_icd11_condition_id?: number | null;
  status?: string | null;
  accepted_ai?: boolean | null;
  notes?: string | null;
  finalized_by_user_id?: number | null;
  finalized_at?: string | null;
  selected_condition_code?: string | null;
  selected_condition_title?: string | null;
  selected_icd11_code?: string | null;
  selected_icd11_title?: string | null;
};

export type TriageLevelDefinition = {
  level: '1' | '2' | '3' | '4_5';
  label: string;
  clinical_presentation: string;
  ed_disposition: string;
};

export type SessionTriageAssessment = {
  id: string;
  session_id: string;
  esi_level: '1' | '2' | '3' | '4_5';
  confidence?: number | null;
  risk_score?: number | null;
  trigger_flags_json?: string[] | null;
  structured_features_json?: Record<string, unknown> | null;
  rationale?: string | null;
  recommended_action?: string | null;
  source: string;
  overridden_by_clinician: boolean;
  override_reason?: string | null;
  updated_by_user_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export const diagnosisAPI = {
  searchDsm: (q: string, limit = 25) =>
    api.get<DiagnosisDisorderSearchRow[]>('/api/diagnosis/dsm/search', { params: { q, limit } }),
  getDisorder: (disorderId: number) =>
    api.get<DiagnosisDisorderDetail>(`/api/diagnosis/dsm/disorders/${disorderId}`),
  resolveIcd11: (disorderId: number, pmoId?: number | null) =>
    api.post<DiagnosisResolveIcd11Response>('/api/diagnosis/dsm/resolve-icd11', {
      disorder_id: disorderId,
      pmo_id: pmoId ?? null,
    }),
  getSelection: (sessionId: string, slot = 'primary') =>
    api.get<SessionDiagnosisSelection | null>(`/api/diagnosis/sessions/${sessionId}/selection`, {
      params: { slot },
    }),
  listSelections: (sessionId: string) =>
    api.get<SessionDiagnosisSelection[]>(`/api/diagnosis/sessions/${sessionId}/selections`),
  putSelection: (
    sessionId: string,
    body: {
      slot?: string;
      dsmv_disorder_id?: number | null;
      pmo_id?: number | null;
      icd11_code?: string | null;
      icd10_code?: string | null;
      diagnosis_status?: string | null;
      ai_suggestion_accepted?: boolean | null;
      assessment_text?: string | null;
      notes?: string | null;
      finalize?: boolean;
    },
  ) => api.put<SessionDiagnosisSelection>(`/api/diagnosis/sessions/${sessionId}/selection`, body),
  deleteSelection: (sessionId: string, slot: string) =>
    api.delete<{ ok: boolean; deleted: boolean }>(`/api/diagnosis/sessions/${sessionId}/selection`, {
      params: { slot },
    }),
  triggerRun: (sessionId: string, source = 'manual') =>
    api.post<DxRunTriggerResponse>(`/api/diagnosis/sessions/${sessionId}/runs/trigger`, null, {
      params: { source },
    }),
  getLatestRun: (sessionId: string) =>
    api.get<DxRunSummary | null>(`/api/diagnosis/sessions/${sessionId}/runs/latest`),
  listCandidates: (sessionId: string, runId?: string) =>
    api.get<DxCandidate[]>(`/api/diagnosis/sessions/${sessionId}/candidates`, {
      params: runId ? { run_id: runId } : undefined,
    }),
  putFinalSelection: (
    sessionId: string,
    body: {
      slot?: string;
      selected_condition_id: number;
      selected_icd11_condition_id?: number | null;
      status?: string | null;
      accepted_ai?: boolean | null;
      notes?: string | null;
    },
  ) => api.put<DxFinalSelection>(`/api/diagnosis/sessions/${sessionId}/final-selections`, body),
  listFinalSelections: (sessionId: string) =>
    api.get<DxFinalSelection[]>(`/api/diagnosis/sessions/${sessionId}/final-selections`),
  deleteFinalSelection: (sessionId: string, slot: string) =>
    api.delete<{ ok: boolean; deleted: boolean }>(`/api/diagnosis/sessions/${sessionId}/final-selections`, {
      params: { slot },
    }),
  listTriageLevels: () =>
    api.get<TriageLevelDefinition[]>(`/api/diagnosis/triage-levels`),
  getTriage: (sessionId: string) =>
    api.get<SessionTriageAssessment | null>(`/api/diagnosis/sessions/${sessionId}/triage`),
  suggestTriage: (sessionId: string) =>
    api.post<SessionTriageAssessment>(`/api/diagnosis/sessions/${sessionId}/triage/suggest`),
  putTriage: (
    sessionId: string,
    body: {
      esi_level: '1' | '2' | '3' | '4_5';
      confidence?: number | null;
      risk_score?: number | null;
      trigger_flags_json?: string[] | null;
      structured_features_json?: Record<string, unknown> | null;
      rationale?: string | null;
      recommended_action?: string | null;
      source?: string;
      override_reason?: string | null;
    },
  ) => api.put<SessionTriageAssessment>(`/api/diagnosis/sessions/${sessionId}/triage`, body),
};

export const glucoseLogAPI = {
  upload: (file: File) => {
    const formData = new FormData();

    formData.append('file', file);

    return api.post(
      '/api/glucose-log/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },
};

// Health check
export const healthCheck = () => api.get('/health');
